/**
 * useRateAlerts — personal Rate Alert email settings for the Mortgage
 * Monitor's Rate Alerts tab. Wraps the manageRateAlerts callable:
 *
 *   { action: 'get' }                → { settings|null, defaults, lastSentAt }
 *   { action: 'save', settings }     → { success, settings }
 *   { action: 'send_test' }          → { success, matched, sent }
 *
 * Admin view-as: pass a targetUid to manage another LO's alerts (the page's
 * user selector). Test emails for another user deliver to the ADMIN's inbox.
 */

import { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../utils/init-firebase';

const functions = getFunctions(app, 'us-east1');

export const RATE_ALERT_LOAN_TYPES = ['Conventional', 'FHA', 'VA', 'USDA', 'NonQM', 'Other'];

export const DEFAULT_TYPE_CRITERIA = {
  enabled: true,
  minRateDeltaBps: null, // null = use the monitor's global threshold
  minMonthlySavings: null,
  minBalance: null,
  maxBalance: null,
};

export const DEFAULT_ALERT_SETTINGS = {
  enabled: false,
  frequency: 'weekly', // 'daily' | 'weekly'
  sendDay: 1,          // 1=Mon … 5=Fri (weekly only)
  loanTypes: RATE_ALERT_LOAN_TYPES.reduce((acc, t) => {
    acc[t] = { ...DEFAULT_TYPE_CRITERIA };
    return acc;
  }, {}),
  includeEquityAlerts: true,
  minEquityPct: null,  // null = use the monitor's global equity flag
  onlyNewSinceLast: false,
};

/** Deep-merge a stored settings payload over the defaults. */
export function mergeAlertSettings(raw) {
  const merged = {
    ...DEFAULT_ALERT_SETTINGS,
    ...(raw || {}),
    loanTypes: {},
  };
  for (const type of RATE_ALERT_LOAN_TYPES) {
    merged.loanTypes[type] = { ...DEFAULT_TYPE_CRITERIA, ...((raw && raw.loanTypes && raw.loanTypes[type]) || {}) };
  }
  return merged;
}

export function useRateAlerts(targetUid = null) {
  const [settings, setSettings] = useState(DEFAULT_ALERT_SETTINGS);
  const [lastSentAt, setLastSentAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, 'manageRateAlerts');
      const res = await call({ action: 'get', targetUid: targetUid || undefined });
      setSettings(mergeAlertSettings(res.data?.settings));
      setLastSentAt(res.data?.lastSentAt || null);
      setDirty(false);
    } catch (err) {
      console.error('manageRateAlerts get failed:', err);
      setError(err.message || 'Failed to load rate alert settings');
    } finally {
      setLoading(false);
    }
  }, [targetUid]);

  useEffect(() => { load(); }, [load]);

  const update = useCallback((patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  const updateType = useCallback((type, patch) => {
    setSettings((prev) => ({
      ...prev,
      loanTypes: { ...prev.loanTypes, [type]: { ...prev.loanTypes[type], ...patch } },
    }));
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const call = httpsCallable(functions, 'manageRateAlerts');
      const res = await call({ action: 'save', settings, targetUid: targetUid || undefined });
      setSettings(mergeAlertSettings(res.data?.settings || settings));
      setDirty(false);
      return { success: true };
    } catch (err) {
      console.error('manageRateAlerts save failed:', err);
      return { success: false, error: err.message || 'Save failed' };
    } finally {
      setSaving(false);
    }
  }, [settings, targetUid]);

  const sendTest = useCallback(async () => {
    setSendingTest(true);
    try {
      const call = httpsCallable(functions, 'manageRateAlerts');
      const res = await call({ action: 'send_test', targetUid: targetUid || undefined });
      return { success: true, ...res.data };
    } catch (err) {
      console.error('manageRateAlerts send_test failed:', err);
      return { success: false, error: err.message || 'Test send failed' };
    } finally {
      setSendingTest(false);
    }
  }, [targetUid]);

  return {
    settings,
    lastSentAt,
    loading,
    saving,
    sendingTest,
    dirty,
    error,
    update,
    updateType,
    save,
    sendTest,
    reload: load,
  };
}

export default useRateAlerts;
